<template>
  <div id="acProject">
    <div v-if="!isLoaded" class="loading">
      <ac-lottie-preloader />
    </div>
    <template v-else>
      <mobile-subscription v-if="isMobileSubscription"/>
      <router-view v-else />

      <push-prompt />
      <post-first-day-modal />
    </template>

    <div id="modals"></div>
    <div id="popups"></div>
    <toast-container />
    <MobAppQRModal v-if="mobileAppDesktopQrModalVisible"/>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted } from 'vue'

// Components
import AcLottiePreloader from '@ui-kit/components/AcLottiePreloader.vue'
import MobileSubscription from '@web/components.v2/MobileSubscription.vue'
import MobAppQRModal from '@web/components/MobAppFeatures/MobAppQRModal.vue'
import ToastContainer from '@web/ui-kit/components/ToastContainer.vue'
import PushPrompt from '@web/platform.v3/components/PushPrompt.vue'
import PostFirstDayModal from '@web/platform.v3/components/post-first-day/PostFirstDayModal.vue'

// Mixins
import useApp from '@web/composition/useApp'
import { useStore } from '@web/store'

// Plugins
import Logger from '@web/common/Logger'
import sentry from '@web/common/sentry'
import { traceLoadApp } from '@web/services/Analytics/events/loadEvents'

import { useRouter } from 'vue-router'
import { mobileAppDesktopQrModal } from './common/ExternalConfig/getters'

export default defineComponent({
  components: {
    MobileSubscription,
    AcLottiePreloader,
    MobAppQRModal,
    ToastContainer,
    PushPrompt,
    PostFirstDayModal
  },
  setup () {
    const store = useStore()
    const router = useRouter()

    const {
      loading,
      logoutHandler,
      offline,
      online,
      title,
      initHandler
    } = useApp({ router })

    const isLoaded = computed(() => !loading.value)
    const isMobileSubscription = computed(() => store.getters['subscription/isMobileSubscription'])
    const mobileAppDesktopQrModalVisible = computed(() => mobileAppDesktopQrModal().visible && isLoaded.value)

    async function init () {
      let appLoading = performance.now()
      const startLoading = Date.now() - 1
      store.commit('assets/domain', window.location.host)

      try {
        await initHandler()
      } catch (e) {
        sentry.captureException(e)
        Logger.error(e)
      } finally {
        appLoading = performance.now() - appLoading
        Logger.info(`App loaded: ${appLoading} ms`)
        traceLoadApp({
          startLoading,
          duration: (Date.now() - startLoading > 0 ? Date.now() : Math.round(appLoading)) || 1
        })
      }

      logoutHandler()

      document.addEventListener('offline', offline, false)
      document.addEventListener('online', online, false)
    }

    onMounted(() => {
      init()
    })

    return {
      title,
      isLoaded,
      isMobileSubscription,
      mobileAppDesktopQrModalVisible
    }
  }
})
</script>

<style lang="scss" src="./assets/scss/theme.scss">
  @import '~nprogress/nprogress';
</style>
<style lang="scss">
  #nprogress .bar {
    background-color: #b03ffb;
    height: 3px;
  }
</style>
